/**
 * Shared third-party script stack for both storefront hosts (task 0055).
 *
 * The host documents no longer carry third-party logic. They emit a single
 * config literal — `window.__SCHA_THIRD_PARTY__` — and the storefront entry
 * runs this installer as its first side effect, before React mounts:
 *
 * - Embedded JSP host (`genericReactPage.jsp`): the literal is emitted inline
 *   from the accelerator page model (server-side values, zero extra requests).
 * - Headless TanStack host (`fe/headless-tanstack`): the literal is written
 *   from `/page-data/bootstrap` + `/page-data/session` before importing the
 *   storefront entry — the same replication pattern as `window.__SCHA_BOOT__`.
 *
 * Gating and ordering mirror the former JSP inline blocks (master.tag was
 * retired with its last consumer — the orphaned /punchout/cxml/inspect
 * route; see docs/features/punchout-ui-liveness.md):
 * OneTrust (consent-gated Hotjar/Dynatrace/Qualtrics/SFMC) → Hotjar identify →
 * `SCHA.feature` host → Kameleoon engine → Zoovu loader → GTM + initial
 * dataLayer → experiment globals → iframeResizer → Qualtrics snippet.
 *
 * A missing or empty literal degrades to the unconditional minimal stack
 * (`SCHA.feature` host, Zoovu loader, empty experiment globals,
 * iframeResizer), so both hosts tolerate absent configuration.
 */

/** One Trust Group Codes used by the consent callbacks (see the JSP comment block). */
const PERFORMANCE_COOKIES = "C0002";
const TARGETING_COOKIES = "C0004";

export const ONE_TRUST_STUB_SRC = "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";
export const HOTJAR_LOADER_PREFIX = "https://static.hotjar.com/c/hotjar-";
export const ZOOVU_USER_TRACKING_SRC =
  "https://services.zoovu.com/events/user-tracking-script/a91628c3-c779-41e8-8fc6-ad6e2ea2f2aa/js-loader";
export const IFRAME_RESIZER_SRC = "https://cdn.schaeffler-ecommerce.com/ecom/common/iframeResizer.min.js";
export const QUALTRICS_ZONE_ID = "ZN_6on1x95deEznjSK";
const QUALTRICS_INTERCEPT_SRC =
  "https://zn6on1x95deeznjsk-schaefflertech.siteintercept.qualtrics.com/SIE/?Q_ZID=ZN_6on1x95deEznjSK";

/** Shape of the `window.__SCHA_THIRD_PARTY__` config literal. All fields optional. */
export interface ThirdPartyConfig {
  oneTrust?: {
    id: string;
    detectLanguage: boolean;
  };
  hotjar?: {
    enabled: boolean;
    hjid: string;
    hjsv: string;
    userType: string;
    userRole: string;
  };
  kameleoon?: {
    enabled: boolean;
    projectCode: string;
    iframeUrl: string;
    userType: string;
    userRole: string;
  };
  gtm?: {
    trackingId: string;
    analyticsDisabled: boolean;
  };
  analytics?: {
    userId: string;
    userRoles: string;
    userHash: string;
    companyName: string;
    salesChannel: string;
    industrialSection: string;
  };
  marketingCloudIdentifier?: string;
  userEmail?: string;
  qualtrics?: {
    enabled: boolean;
    language: string;
  };
  /** Prerender crawler suppression (server-detected in the JSP host, UA-detected in headless). */
  prerender?: boolean;
}

export type ThirdPartyScriptStep =
  | {
      kind: "oneTrust";
      oneTrustId: string;
      detectLanguage: boolean;
      /** Hotjar site id + snippet version, loaded on performance-cookie consent. */
      hotjar: { hjid: string; hjsv: string } | null;
      /** SFMC Marketing Cloud org id; the targeting-consent branch is emitted only when set. */
      marketingCloudIdentifier: string | null;
      userEmail: string;
    }
  | { kind: "hotjarIdentify"; userType: string; userRole: string | null }
  | { kind: "featureHost" }
  | { kind: "kameleoon"; projectCode: string; iframeUrl: string }
  | { kind: "zoovuLoader" }
  | { kind: "gtm"; trackingId: string; dataLayerInit: Record<string, string> }
  | { kind: "experimentGlobals"; qualtricsLanguage: string; kameleoonUserType: string; kameleoonUserRole: string }
  | { kind: "iframeResizer" }
  | { kind: "qualtrics" };

function str(value: string | null | undefined): string {
  return value ?? "";
}

/**
 * Builds the ordered third-party script plan for one document boot from the
 * config literal. Page-scoped analytics context (breadcrumb `pageCategory`,
 * `searchKeyword`, `PLP_productCategorization`) is deliberately empty in the
 * initial dataLayer: the host cannot know the page at boot. The storefront
 * router pushes the real values per committed route transition instead —
 * see `app/analytics/pageContext` (task 0061).
 */
export function buildThirdPartyScriptPlan(config: ThirdPartyConfig | null | undefined): ThirdPartyScriptStep[] {
  const oneTrust = config?.oneTrust;
  const hotjar = config?.hotjar;
  const kameleoon = config?.kameleoon;
  const gtm = config?.gtm;
  const analytics = config?.analytics;
  const qualtrics = config?.qualtrics;
  const prerender = config?.prerender === true;

  const hotjarEnabled = hotjar?.enabled === true;
  const hjidValue = str(hotjar?.hjid);
  const hjsvValue = str(hotjar?.hjsv);
  const kameleoonEnabled = kameleoon?.enabled === true;
  const kameleoonProjectCode = str(kameleoon?.projectCode);
  const kameleoonIframeUrl = str(kameleoon?.iframeUrl);
  const googleAnalyticsTrackingId = str(gtm?.trackingId);
  const marketingCloudIdentifier = str(config?.marketingCloudIdentifier);

  const steps: ThirdPartyScriptStep[] = [];

  if (oneTrust?.id && !prerender) {
    steps.push({
      kind: "oneTrust",
      oneTrustId: oneTrust.id,
      detectLanguage: oneTrust.detectLanguage === true,
      hotjar: hotjarEnabled && hjidValue && hjsvValue ? { hjid: hjidValue, hjsv: hjsvValue } : null,
      marketingCloudIdentifier: marketingCloudIdentifier || null,
      userEmail: str(config?.userEmail),
    });
  }

  if (hotjarEnabled) {
    steps.push({
      kind: "hotjarIdentify",
      userType: str(hotjar?.userType),
      userRole: hotjar?.userRole || null,
    });
  }

  // Unconditional in the former JSP blocks: the transitional Kameleoon flag
  // host must exist before both the engine and the React entry read/write it.
  steps.push({ kind: "featureHost" });

  if (kameleoonEnabled && kameleoonProjectCode && kameleoonIframeUrl) {
    steps.push({ kind: "kameleoon", projectCode: kameleoonProjectCode, iframeUrl: kameleoonIframeUrl });
  }

  steps.push({ kind: "zoovuLoader" });

  if (googleAnalyticsTrackingId && gtm?.analyticsDisabled !== true && !prerender) {
    steps.push({
      kind: "gtm",
      trackingId: googleAnalyticsTrackingId,
      dataLayerInit: {
        userID: str(analytics?.userId),
        userRole: str(analytics?.userRoles),
        kameleoonUserRole: str(kameleoon?.userRole),
        kameleoonUserType: str(kameleoon?.userType),
        userIndustrialSector: str(analytics?.industrialSection),
        userSalesChannel: str(analytics?.salesChannel),
        userHash: str(analytics?.userHash),
        pageCategory: "",
        searchKeyword: "",
        userCompany: str(analytics?.companyName),
        PLP_productCategorization: "",
        PDP_designation: "",
        PDP_baseType: "",
        PDP_productID: "",
        PDP_preferredProduct: "",
      },
    });
  }

  steps.push({
    kind: "experimentGlobals",
    qualtricsLanguage: str(qualtrics?.language),
    kameleoonUserType: str(kameleoon?.userType),
    kameleoonUserRole: str(kameleoon?.userRole),
  });

  steps.push({ kind: "iframeResizer" });

  if (qualtrics?.enabled === true) {
    steps.push({ kind: "qualtrics" });
  }

  return steps;
}

/**
 * Entry-point side effect: installs the stack described by the host's
 * `window.__SCHA_THIRD_PARTY__` literal. Runs synchronously through the
 * iframeResizer insertion; only the iframeResizer load wait (ordering parity
 * with the former parse-blocking script tag) and the trailing Qualtrics
 * snippet defer past the call — React mounting is never blocked on network.
 *
 * Idempotent per document: a second call (module re-evaluation, host re-boot)
 * is a no-op, mirroring the single inline copy the JSP documents carried.
 */
export function runThirdPartyBoot(): void {
  const w = window as unknown as ThirdPartyWindow;
  if (w.__SCHA_THIRD_PARTY_INSTALLED__) {
    return;
  }
  w.__SCHA_THIRD_PARTY_INSTALLED__ = true;

  const steps = buildThirdPartyScriptPlan(w.__SCHA_THIRD_PARTY__);
  void installThirdPartyScripts(steps);
}

/** Installs the plan in order. Thin on purpose: all decisions live in the plan builder. */
export async function installThirdPartyScripts(steps: ThirdPartyScriptStep[]): Promise<void> {
  for (const step of steps) {
    switch (step.kind) {
      case "oneTrust":
        installOneTrust(step);
        break;
      case "hotjarIdentify":
        installHotjarIdentify(step);
        break;
      case "featureHost":
        installFeatureHost();
        break;
      case "kameleoon":
        installKameleoon(step);
        break;
      case "zoovuLoader":
        appendScript({ src: ZOOVU_USER_TRACKING_SRC });
        break;
      case "gtm":
        installGtm(step);
        break;
      case "experimentGlobals":
        installExperimentGlobals(step);
        break;
      case "iframeResizer":
        // The former JSP host loaded iframeResizer synchronously before React
        // executed; await its load (bounded, failures pass through) for
        // ordering parity without blocking React mount.
        await appendScriptAndWait({ src: IFRAME_RESIZER_SRC });
        break;
      case "qualtrics":
        installQualtrics();
        break;
    }
  }
}

/* ------------------------------------------------------------------ installer */

interface ThirdPartyWindow {
  __SCHA_THIRD_PARTY__?: ThirdPartyConfig;
  __SCHA_THIRD_PARTY_INSTALLED__?: boolean;
  SCHA?: Record<string, unknown> & {
    feature?: Record<string, unknown>;
    kameleoon?: { userType?: string; userRole?: string };
    pageCategory?: string;
  };
  OnetrustActiveGroups?: string;
  OptanonWrapper?: () => void;
  _etmc?: unknown[][];
  _etmcPromise?: Promise<boolean>;
  _bannerInteractedPromise?: Promise<unknown>;
  hj?: HotjarStub;
  dtrum?: { enable: () => void };
  QSI?: { API: { load: () => void; run: () => void; unload: () => void } };
  startedQualtrics?: boolean;
  startQualtrics?: () => void;
  updateMICookieConsent?: () => void;
  qualtricsLanguage?: string;
  dataLayer?: Record<string, string>[];
  kameleoonQueue?: unknown[];
}

/** Hotjar command queue stub, mirroring the former JSP's `hj.q` buffering. */
type HotjarStub = ((...args: unknown[]) => void) & { q?: unknown[][] };

function createHotjarStub(): HotjarStub {
  const stub = ((...args: unknown[]) => {
    (stub.q = stub.q ?? []).push(args);
  }) as HotjarStub;
  return stub;
}

function appendScript(options: {
  src: string;
  async?: boolean;
  attributes?: Record<string, string>;
}): HTMLScriptElement {
  const script = document.createElement("script");
  script.src = options.src;
  script.async = options.async ?? true;
  for (const [name, value] of Object.entries(options.attributes ?? {})) {
    script.setAttribute(name, value);
  }
  document.head.appendChild(script);
  return script;
}

function appendScriptAndWait(options: { src: string; timeoutMs?: number }): Promise<void> {
  const timeoutMs = options.timeoutMs ?? 5_000;
  return new Promise((resolve) => {
    const script = appendScript({ src: options.src });
    const done = (): void => resolve();
    script.onload = done;
    script.onerror = done;
    setTimeout(done, timeoutMs);
  });
}

/** Mirrors the former JSP inline `loadScript` helper (head-appended, async). */
function loadScript(src: string): void {
  appendScript({ src });
}

function isGroupEnabled(code: string): boolean {
  const w = window as unknown as ThirdPartyWindow;
  return (w.OnetrustActiveGroups ?? "").indexOf(code) !== -1;
}

function installOneTrust(step: Extract<ThirdPartyScriptStep, { kind: "oneTrust" }>): void {
  const w = window as unknown as ThirdPartyWindow;

  let etmcResolve!: (allowed: boolean) => void;
  w._etmcPromise = new Promise<boolean>((resolve) => {
    etmcResolve = resolve;
  });

  let bannerResolve!: (interacted: unknown) => void;
  w._bannerInteractedPromise = new Promise((resolve) => {
    bannerResolve = resolve;
  });

  w.OptanonWrapper = () => {
    if (document.cookie.includes("OptanonAlertBoxClosed")) {
      bannerResolve(true);
    }

    if (isGroupEnabled(PERFORMANCE_COOKIES)) {
      if (step.hotjar) {
        w.hj = w.hj ?? createHotjarStub();
        loadScript(`${HOTJAR_LOADER_PREFIX}${step.hotjar.hjid}.js?sv=${step.hotjar.hjsv}`);
      }

      // Dynatrace opt-in: defensive, the agent itself is not loaded by the host document.
      w.dtrum?.enable();

      if (!w.startedQualtrics) {
        w.startQualtrics?.();
        w.startedQualtrics = true;
      } else if (w.QSI) {
        w.QSI.API.load();
        w.QSI.API.run();
      }
    } else if (w.QSI) {
      w.QSI.API.unload();
    }

    if (step.marketingCloudIdentifier) {
      if (isGroupEnabled(TARGETING_COOKIES)) {
        loadScript(`https://${step.marketingCloudIdentifier}.collect.igodigital.com/collect.js`);
        w._etmc = w._etmc ?? [];
        w._etmc.push(["setOrgId", step.marketingCloudIdentifier]);
        w._etmc.push(["setUserInfo", { email: step.userEmail }]);
        etmcResolve(true);
      } else {
        w._etmc = w._etmc ?? [];
        w._etmc.push(["doNotTrack"]);
        etmcResolve(false);
      }
    }

    w.updateMICookieConsent?.();
  };

  // NOTE (task 0055 decision 6): the stub loads async (dynamically inserted)
  // instead of parse-blocking, so the consent banner appears marginally later
  // than in the former inline JSP version. Accepted; revisit only if the
  // privacy owner asks for the earlier timing back.
  appendScript({
    src: ONE_TRUST_STUB_SRC,
    async: false,
    attributes: {
      type: "text/javascript",
      charset: "UTF-8",
      "data-domain-script": step.oneTrustId,
      ...(step.detectLanguage ? { "data-document-language": "true" } : {}),
    },
  });
}

function installHotjarIdentify(step: Extract<ThirdPartyScriptStep, { kind: "hotjarIdentify" }>): void {
  const w = window as unknown as ThirdPartyWindow;
  const hj = (w.hj = w.hj ?? createHotjarStub());
  hj(
    "identify",
    null,
    step.userRole ? { userType: step.userType, userRole: step.userRole } : { userType: step.userType },
  );
}

function installFeatureHost(): void {
  const w = window as unknown as ThirdPartyWindow;
  w.SCHA = w.SCHA ?? {};
  w.SCHA.feature = w.SCHA.feature ?? {};
}

function installKameleoon(step: Extract<ThirdPartyScriptStep, { kind: "kameleoon" }>): void {
  const w = window as unknown as ThirdPartyWindow & {
    kameleoonIframeURL?: string;
    kameleoonLightIframe?: boolean;
    kameleoonIframeOrigin?: string;
    Kameleoon?: unknown;
    kameleoonExternalIFrameLoaded?: boolean;
    kameleoonExternalIFrameLoadedData?: unknown;
  };

  w.kameleoonIframeURL = step.iframeUrl;
  w.kameleoonLightIframe = false;

  const originElement = document.createElement("a");
  originElement.href = step.iframeUrl;
  w.kameleoonIframeOrigin = originElement.origin || `${originElement.protocol}//${originElement.hostname}`;

  if (!location.href.startsWith(w.kameleoonIframeOrigin)) {
    w.kameleoonLightIframe = true;
    const processMessageEvent = (event: MessageEvent): void => {
      const data = event.data as string | undefined;
      if (w.kameleoonIframeOrigin === event.origin && data?.slice?.(0, 9) === "Kameleoon") {
        window.removeEventListener("message", processMessageEvent);
        w.kameleoonExternalIFrameLoaded = true;
        if (w.Kameleoon) {
          const engine = w.Kameleoon as {
            Utils: { runProtectedScript: (script: string) => void };
            Analyst: { load: () => void };
          };
          engine.Utils.runProtectedScript(data);
          engine.Analyst.load();
        } else {
          w.kameleoonExternalIFrameLoadedData = data;
        }
      }
    };
    window.addEventListener("message", processMessageEvent, false);

    const iframeNode = document.createElement("iframe");
    iframeNode.src = step.iframeUrl;
    iframeNode.id = "kameleoonExternalIframe";
    iframeNode.setAttribute(
      "style",
      "float: left !important; opacity: 0.0 !important; width: 0px !important; height: 0px !important;",
    );
    document.head.appendChild(iframeNode);
  }

  w.kameleoonQueue = w.kameleoonQueue ?? [];
  appendScript({
    // Protocol-relative on purpose: the former JSP used `//${code}.kameleoon.io/engine.js`.
    // NOTE (task 0055 decision 5): inserted at module time instead of parse
    // time — the later start (experiment flicker risk) was consciously accepted.
    src: `//${step.projectCode}.kameleoon.io/engine.js`,
    async: true,
    attributes: { fetchpriority: "high" },
  });
}

function installGtm(step: Extract<ThirdPartyScriptStep, { kind: "gtm" }>): void {
  const w = window as unknown as ThirdPartyWindow;
  w.SCHA = w.SCHA ?? {};
  w.SCHA.pageCategory = "";
  w.dataLayer = [step.dataLayerInit];
  appendScript({ src: `https://www.googletagmanager.com/gtm.js?id=${step.trackingId}` });
}

function installExperimentGlobals(step: Extract<ThirdPartyScriptStep, { kind: "experimentGlobals" }>): void {
  const w = window as unknown as ThirdPartyWindow;
  w.qualtricsLanguage = step.qualtricsLanguage;
  w.SCHA = w.SCHA ?? {};
  w.SCHA.kameleoon = { userType: step.kameleoonUserType, userRole: step.kameleoonUserRole };
}

/**
 * Qualtrics Website Feedback loader, a clean port of the former JSP's verbatim
 * vendor snippet (zone {@link QUALTRICS_ZONE_ID}, `r`-type creative, 100%
 * sampling): defines `window.startQualtrics` for the consent callback and
 * installs the intercept container.
 */
function installQualtrics(): void {
  const w = window as unknown as ThirdPartyWindow;
  const samplingCookieName = `QSI_S_${QUALTRICS_ZONE_ID}`;

  const readSamplingCookie = (): string[] | null => {
    const prefix = `${samplingCookieName}=`;
    for (const raw of document.cookie.split(";")) {
      const part = raw.replace(/^ /, "");
      if (part.startsWith(prefix)) {
        return part.slice(prefix.length).split(":");
      }
    }
    return null;
  };

  const writeSamplingCookie = (value: string): void => {
    const expiry = new Date(Date.now() + 6048e5);
    document.cookie = `${samplingCookieName}=${value};expires=${expiry.toUTCString()};path=/; `;
  };

  const check = (): boolean => {
    const parts = readSamplingCookie();
    if (parts === null) {
      // The vendor configuration uses e=100: no stored sample decision means "run".
      return true;
    }
    const rate = Number(parts[1]);
    if (rate === 100) {
      return true;
    }
    if (parts[0] === "v") {
      return false;
    }
    if (parts[0] === "r") {
      const interval = Math.floor(100 / rate);
      const hits = Number(parts[2] ?? 0) + 1;
      parts[2] = String(hits);
      writeSamplingCookie(parts.join(":"));
      return hits % interval === 0;
    }
    return true;
  };

  const go = (): void => {
    if (check()) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = QUALTRICS_INTERCEPT_SRC;
      document.body.appendChild(script);
    }
  };

  w.startQualtrics = () => {
    if (document.readyState !== "complete") {
      window.addEventListener("load", go, false);
    } else {
      go();
    }
  };

  const container = document.createElement("div");
  container.id = QUALTRICS_ZONE_ID;
  document.body.appendChild(container);
}
